<template>
	<div>
		<advanced-filters v-if="!modal" :module="module" :loading-state="loadingState" :has-filters="(listOptions.filters || []).length || !!searchTerm" :filtered-count="rowCount" v-model="listOptions">
			<div class="search-box"><input type="text" v-model="searchTerm" ref="search" :placeholder="`Search ${module.name}`" /></div>
		</advanced-filters>

		<drawer v-if="!modal" :name="DRAWERS.LIST_TRIGGERS" :shrink="true">
			<list-triggers-drawer v-if="listTriggerRow" :module="module" :row="listTriggerRow"></list-triggers-drawer>
		</drawer>

		<div>
			<div class="buttons">
				<slot name="create"></slot>
			</div>
			<div class="title">
				<slot name="title"></slot>
			</div>
		</div>

		<nothing-here v-if="!module.has_paging && rows.length === 0 && loadingState.state === 'LOADED'"></nothing-here>
		<nothing-here v-else-if="filteredRows.length === 0 && loadingState.state === 'LOADED'" text="No results found"></nothing-here>

		<div v-if="loadingState.state === 'LOADING'" class="loader inline"><img src="/images/spinner.svg" class="fa fa-spin spinner" aria-hidden="true"></div>

		<div class="table-scroll-wrapper">
			<div v-if="loadingState.state === 'LOADED'" class="table list">
				<div v-if="filteredRows.length > 0" class="row sortable">
					<div v-if="canDragAndDrop" class="cell buttons">
						<a v-if="!isOrdering" href @click.prevent="enableDragging" class="button" data-ytta-id="-"><div class="hover-title">Custom Sort</div><span class="fa fa-sort" aria-hidden="true"></span></a>
						<a v-else href @click.prevent="isOrdering = false" class="button" data-ytta-id="-"><div class="hover-title">Stop Sorting</div><span class="fa fa-save" aria-hidden="true"></span></a>
					</div>
					<div v-else-if="!module.sort" class="cell buttons">
						<a href @click.prevent="resetSortingAndDragging" class="button"><div class="hover-title">{{ resetButtonText }}</div><span class="fa fa-sync" aria-hidden="true"></span></a>
					</div>
					<div v-else class="cell">Actions</div>
					<div v-if="module.show_id" class="cell not-actionable">ID</div>
					<div v-for="field in indexFields" :key="`field-heading-index-${ field.id }`" @click="sortByColumn(field.slug)" class="cell" :class="headerColumnClass(field)">
						{{ field.name }}
						<span v-if="sort.column === field.slug" class="fa" :class="sort.direction === 'asc' ? 'fa-angle-down' : 'fa-angle-up'"></span>
					</div>
				</div>

				<tbody v-sortable="{ onUpdate: reorderRows, handle: '.row', disabled: !isOrdering, scroll: true }">
				<div v-for="row in filteredRows" class="row" :class="{ 'crud-item': isOrdering }" :key="row.id">
					<div class="cell buttons">
						<delete-button :can-delete="!!(module.can_delete || $root.isSuperAdmin)" @deleted="(done) => deleteRow(row, done)">
							<a v-if="module.can_edit || $root.isSuperAdmin" :href="`/module/${module.slug}/${row.id}/edit`" @click.prevent="onEditRow(row)" class="button"><div class="hover-title">{{ module.children.length > 0 ? 'Manage' : 'Update' }}</div><span class="fa fa-pencil"></span></a>
							<a v-if="module.can_copy" href @click.prevent="onCopyRow(row)" class="button" :class="copyingRow ? 'disabled not-clickable' : ''"><div class="hover-title">{{ copyingRow === row.id ? '' : 'Copy' }}</div><span class="fa" :class="copyingRow === row.id ? 'fa-sync fa-spin' : 'fa-copy'"></span></a>

							<a href v-if="triggers.length > 1" @click.prevent="() => toggleRowTriggerDrawer(row)" class="button"><div class="hover-title">Show Actions</div><span class="fa fa-location" ></span></a>
							<trigger-button v-for="trigger in (triggers.length > 1 ? [] : triggers)" :key="`${ module.id }-row-${ row.id }-trigger-${ trigger.id }`" :trigger="trigger" :module="module" :row="row" location="index_rows"></trigger-button>

							<router-link v-if="hasGallery" :to="`${ module.url_path_prefix }${ module.slug }/gallery/${ row.id }`" class="button"><div class="hover-title">Photos</div><span class="fa fa-camera"></span></router-link>
							<router-link v-if="hasFileGallery" :to="`${ module.url_path_prefix }${ module.slug }/file-gallery/${ row.id }`" class="button"><div class="hover-title">Files</div><span class="fa fa-upload"></span></router-link>
						</delete-button>
					</div>
					<div v-if="module.show_id" class="cell">{{ row.id }}</div>
					<div v-for="field in indexFields" :key="`row-${ row.id }-field-${ field.id }`" class="cell" :class="field.column_style">
						<component :is="getFieldComponent(field)" :field="field" :row="row"></component>
					</div>
				</div>
				</tbody>
			</div>
		</div>
		<div v-if="module.has_paging && paging && paging.last_page > 1" class="paginate">
			<div v-if="paging && paging.current !== 1" class="left-links"><a href @click.prevent="selectPage(paging.current - 1)">Previous</a></div>
			<div class="center-links">
				<a v-for="n in paging.last_page" :key="`page-${n}`" href @click.prevent="selectPage(n)" :class="(n) === listOptions.page ? 'active' : ''">{{ n }}</a>
			</div>
			<div v-if="paging && paging.current !== paging.last_page" class="right-links"><a href @click.prevent="selectPage(paging.current + 1)">Next</a></div>
		</div>
	</div>
</template>

<script>

import _ from 'underscore';
import * as Fields from './fields/Outputs';
import { DRAWERS } from '@/constants';
import moment from 'moment';
import Vue2Filters from 'vue2-filters';
import DeleteButton from '@/views/components/DeleteButton';
import TriggerButton from '../../../../components/TriggerButton';
import AdvancedFilters from '@/views/sections/app/modules/components/filters/AdvancedFilters';
import ListTriggersDrawer from '@/views/sections/app/modules/drawers/ListTriggersDrawer';

const UNSORTABLE_FIELD_TYPES = ['wysiwyg', 'photo', 'file', 'tags', 'associated_module'];

// interface ListComponentData {
// 	sort: {
// 		column: boolean | string;
// 		direction: boolean | string;
// 		field: object;
// 	}
// 	usingFilters: boolean;
// 	listOptions: ListOptions;
// 	copyingRow: null | string;
// 	isOrdering: boolean;
// 	DRAWERS: Record<string, string>;
// }

export default {

	props: ['rows', 'loadingState', 'paging', 'searchable', 'module', 'hasGallery', 'hasFileGallery', 'modal'],

	mixins: [Vue2Filters.mixin],

	data() {
		return {
			sort: {
				column: false,
				direction: false,
				field: {},
			},

			// Whether or not using server-side filtering on this page load
			usingFilters: false,

			listOptions: _.clone(this.loadingState.loadingOptions || this.loadingState.loadedOptions),

			// copyingRow will be set to a row id if copying is running
			copyingRow: null,

			// Whether or not they are actively drag & drop sorting
			isOrdering: false,

			searchTerm: '',
			filters: [],
			DRAWERS,

			// The row that's been selected to show the triggers drawer (when multiple triggers per line item)
			listTriggerRow: null,
		};
	},

	created() {
		this.resetSort();
	},

	mounted() {
		if (this.$refs.search) {
			this.$refs.search.focus();
		}
	},

	watch: {
		rows() {
			if (!this.module.has_paging) {
				this.resetSort();
			}
		},
		searchTerm(newSearchTerm) {
			if (this.module.has_paging || this.usingFilters) {
				this.$set(this.listOptions, 'searchTerm', newSearchTerm);
			}
		},

		listOptions: {
			deep: true,
			handler() {
				this.usingFilters = true;
				this.updateListOptions();
			}
		},
	},

	computed: {

		indexFields() {
			if (!this.module.fields) {
				return [];
			}

			return this.module.fields.filter(field => {
				return !!field.on_index;
			});
		},

		canDragAndDrop() {
			return !this.module.has_paging && !this.module.sort && (this.sort.column === false && !this.searchTerm);
		},

		sortedRows() {
			if (this.module.has_paging) {
				return this.rows;
			}

			if (this.sort.column !== false && this.sort.direction !== false) {
				let sorted = _.sortBy(this.rows, (row) => {
					// Sort based on field type
					switch (this.sort.field.type) {
						case 'date':
							return moment(row[ this.sort.column ]).unix();
						default:
							if (typeof row[ this.sort.column ] === 'string') {
								return row[ this.sort.column ].toLowerCase();
							}
							return row[ this.sort.column ];
					}
				});

				if (this.sort.direction === 'desc') {
					sorted = sorted.reverse();
				}

				return sorted;
			}

			return this.rows;
		},

		filteredRows() {
			if (this.module.has_paging) {
				return this.rows;
			}

			return this.filterBy(this.sortedRows, this.searchTerm);
		},

		rowCount() {
			if ((this.module.has_paging || this.usingFilters) && this.paging) {
				return this.paging.total;
			}

			return this.filteredRows.length;
		},

		resetButtonText() {
			let text = [];

			if (this.sort.column) {
				text.push('Reset Sorting');
			}

			if (this.searchTerm) {
				text.push('Clear Search');
			}

			return text.join(' & ');
		},

		primaryField() {
			const allowedFieldTypes = ['string'];
			for (let field of this.indexFields) {
				if (allowedFieldTypes.indexOf(field.type) !== -1) {
					return field;
				}
			}

			return false;
		},

		triggers() {
			return this.module.triggers.filter(trigger => {
				return trigger.button_locations.includes('index_rows');
			});
		},
	},

	methods: {
		saveSort() {
			const order = this.sortedRows.map((row, index) => {
				return { id: row.id, order: index };
			});

			this.$api.post(`/module/${this.module.slug}/order`, { order }).then(() => {
				this.$store.dispatch('addToast', 'Sort Position Saved');
			});
		},

		enableDragging() {
			this.isOrdering = true;
			this.sort.column = false;
			this.$forceUpdate();
		},

		resetSortingAndDragging() {
			this.isOrdering = false;
			this.resetSort();
			this.searchTerm = '';
		},

		reorderRows(event) {
			this.sortedRows.splice(event.newIndex, 0, this.sortedRows.splice(event.oldIndex, 1)[ 0 ]);
			this.saveSort();
		},

		selectPage(pageNumber) {
			this.$set(this.listOptions, 'page', pageNumber);
		},

		onEditRow(row) {
			if (this.modal) {
				return this.openEditModal(row);
			}

			this.$router.push(`/module/${this.module.slug}/${row.id}/edit`);
		},

		async onCopyRow(row) {
			if (this.copyingRow) return;

			this.copyingRow = row.id;

			try {
				const { data } = await this.$api.post(`/module/${this.module.slug}/${row.id}/copy`);
				if (data.id) {
					this.$store.dispatch('addToast', 'Row Copied');
					this.onEditRow(data);
				}
			} catch (e) {
				this.$store.dispatch('errorToast', 'Unable to copy row. Please contact support. Error: ' + e.message);
			}

			this.copyingRow = null;
		},

		/**
		 * Open the edit page in a modal
		 */
		openEditModal(row) {
			this.$root.$emit('open-drawer', DRAWERS.MODULE_FORM, {
				module: this.module,
				id: row.id,
				formData: row,
				isUpdate: true,
			});
		},

		deleteRow(row, done) {
			this.$api.post(`/module/${this.module.slug}/${row.id}/delete`).then(() => {
				this.$store.dispatch('errorToast', this.module.singular_name + ' Deleted');
				this.$store.dispatch('loadModuleCounts');
				this.$emit('removed', row);

				done();
			});
		},

		headerColumnClass(field) {
			const canSort = this.canSortColumn(field);
			let classes = [field.column_style, canSort ? 'sortable' : 'not-actionable'];

			if (canSort) {
				if (this.sort.column == field.slug) {
					if (this.sort.direction === 'asc') {
						classes.push('point-up');
					}
				} else {
					classes.push('point-down');
				}
			}

			return classes.join(' ');
		},

		canSortColumn(field) {
			return (UNSORTABLE_FIELD_TYPES.indexOf(field.type) === -1);
		},

		sortByColumn(columnName) {
			const field = _.findWhere(this.indexFields, { slug: columnName });

			if (!this.canSortColumn(field)) {
				return;
			}

			const sortOptions = ['asc', 'desc', false];

			this.isOrdering = false; // Ensure they cannot drag & drop

			let direction = -1;
			if (this.sort.column === columnName) {
				direction = sortOptions.indexOf(this.sort.direction);
			}

			direction++;

			if (!sortOptions[ direction ]) {
				columnName = false;
			}

			this.sort = {
				column: columnName,
				direction: sortOptions[ direction ],
				field,
			};

			if (this.module.has_paging) {
				this.listOptions = {
					...this.listOptions,
					sort: {
						...this.sort,
					},
					page: 1,
				};
			}
		},

		updateListOptions() {
			this.$emit('updated', this.listOptions);
		},

		resetSort() {
			this.sort.column = false;
			this.sort.direction = false;

			if (this.module.sort) {
				const [column, direction] = this.module.sort.split(' ');

				this.sort.column = column;
				this.sort.direction = direction ? direction.toLowerCase() : 'asc';
			}
		},

		toggleRowTriggerDrawer(row) {
			this.listTriggerRow = row;
			this.$root.$emit('toggle-drawer', DRAWERS.LIST_TRIGGERS);
		},

		/**
		 * Get the Output component for this field (based on field type)
		 *
		 * @param field
		 * @returns {*}
		 */
		getFieldComponent(field) {
			if (typeof Fields[ field.type ] !== 'undefined') {
				return Fields[ field.type ];
			}

			return Fields[ 'string' ];
		},
	},

	components: {
		AdvancedFilters,
		TriggerButton,
		DeleteButton,
		ListTriggersDrawer,
	},

};

</script>
